import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { FileSignature, Plus, Pencil, CalendarX2 } from 'lucide-react';
import api from '../../../utils/api';
import {
  Panel, Pill, RowMenu, Banner, Sheet, Modal, Field, Input, Textarea, Button, LoadingBlock, ErrorBlock,
  money, useLoad, errMsg, fmtDate, todayIso,
} from '../kit';
import { LinkButton } from './shared';

// The participant's support coordination service agreement (spec 6.6): the dates their time is
// claimed under and the hourly rate claims and invoices use. Listed on Plan & Budgets with Add,
// Edit and End; the setup checklist's service agreement Fix opens the same form (AgreementForm).
// Nothing is deleted: ending an agreement sets its last day and keeps it on file.

const STATE = {
  current: { label: 'Current', tone: 'emerald' },
  future: { label: 'Starts later', tone: 'blue' },
  ended: { label: 'Ended', tone: 'slate' },
  cancelled: { label: 'Cancelled', tone: 'slate' },
};
const canChange = (perms) => (perms || []).includes('manage_referrals') || (perms || []).includes('admin');
const addDays = (iso, n) => {
  const [y, m, d] = String(iso || '').slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};
const perHour = (v) => `${money(v)} an hour`;

// Today's NDIS price limit for the form: one figure, or a range when the participant has two items.
function limitText(priceLimits) {
  const known = (priceLimits || []).map((p) => Number(p.limit)).filter((n) => n > 0);
  if (!known.length) return '';
  const lo = Math.min(...known); const hi = Math.max(...known);
  return lo === hi ? `NDIS price limit today: ${perHour(lo)}.` : `NDIS price limits today: ${money(lo)} to ${perHour(hi)}.`;
}

// Warnings that come back with a save (price list gaps, overlaps, time left uncovered).
export function showAgreementWarnings(warnings) {
  (warnings || []).forEach((w) => toast(w, { duration: 9000 }));
}
// A save from the setup checklist refreshes the Plan & Budgets panel too (and the other way round
// through the file's onChanged).
const SAVED_EVENT = 'sc:agreement-saved';
const announce = (coordinationId) => { try { window.dispatchEvent(new CustomEvent(SAVED_EVENT, { detail: { coordinationId } })); } catch { /* old browsers */ } };

// Add (agreement = null) or change an agreement. Used in the Plan & Budgets sheet and inline in
// the setup checklist. onSaved gets the server's { agreement, warnings, checklist }.
export function AgreementForm({ coordinationId, agreement = null, defaults = {}, priceLimits, onSaved, onCancel, saveLabel }) {
  const editing = !!(agreement && agreement.id);
  const [f, setF] = useState(() => ({
    date_from: (editing ? agreement.date_from : defaults.date_from) || '',
    date_to: (editing ? agreement.date_to : defaults.date_to) || '',
    hourly_rate: (editing ? agreement.hourly_rate : defaults.hourly_rate) ?? '',
    notes: (editing ? agreement.notes : '') || '',
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [tried, setTried] = useState(false);
  const set = (k) => (e) => { const v = e && e.target ? e.target.value : e; setF((p) => ({ ...p, [k]: v })); setError(''); };

  const rate = Number(f.hourly_rate);
  const problems = {
    date_from: !f.date_from ? 'Add the start date.' : '',
    date_to: !f.date_to ? 'Add the end date.' : (f.date_from && f.date_to < f.date_from ? 'The end date is before the start date.' : ''),
    hourly_rate: f.hourly_rate === '' || !(rate > 0) ? 'Add the hourly rate (more than $0).' : '',
  };
  const invalid = Object.values(problems).some(Boolean);
  const limit = limitText(priceLimits);

  const save = async () => {
    setTried(true);
    if (invalid) return;
    setBusy(true); setError('');
    const body = { date_from: f.date_from, date_to: f.date_to, hourly_rate: rate, notes: f.notes.trim() };
    try {
      const r = editing
        ? await api.put(`/sc/engagements/${coordinationId}/coordination-agreements/${agreement.id}`, body)
        : await api.post(`/sc/engagements/${coordinationId}/coordination-agreements`, body);
      toast.success(editing ? 'Service agreement saved' : 'Service agreement added');
      showAgreementWarnings(r.data && r.data.warnings);
      announce(coordinationId);
      onSaved?.(r.data);
    } catch (e) {
      setError(errMsg(e, 'The agreement wasn’t saved. Try again.'));
    } finally { setBusy(false); }
  };

  return (
    <form noValidate onSubmit={(e) => { e.preventDefault(); save(); }} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label="Starts" required error={tried ? problems.date_from : ''}>
          <Input type="date" value={f.date_from} onChange={set('date_from')} />
        </Field>
        <Field label="Ends" required error={tried ? problems.date_to : ''}>
          <Input type="date" value={f.date_to} min={f.date_from || undefined} onChange={set('date_to')} />
        </Field>
        <Field label="Hourly rate ($)" required error={tried ? problems.hourly_rate : ''} hint={limit || undefined}>
          <Input type="number" inputMode="decimal" min="0" step="0.01" value={f.hourly_rate} onChange={set('hourly_rate')} />
        </Field>
      </div>
      <Field label="Notes" hint="For example where the signed agreement is kept.">
        <Textarea rows={2} maxLength={2000} value={f.notes} onChange={set('notes')} />
      </Field>
      {editing && <p className="text-[12.5px] text-slate-500">Time already claimed keeps the rate it was claimed at. Unclaimed time uses the new dates and rate.</p>}
      {error && <Banner tone="red">{error}</Banner>}
      <div className="flex flex-wrap justify-end gap-2">
        {onCancel && <Button variant="ghost" onClick={onCancel}>Cancel</Button>}
        <Button type="submit" loading={busy}>{saveLabel || (editing ? 'Save changes' : 'Add agreement')}</Button>
      </div>
    </form>
  );
}

function EndModal({ coordinationId, agreement, onClose, onDone }) {
  const today = todayIso();
  const lastAllowed = agreement ? addDays(agreement.date_to, -1) : '';
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!agreement) return;
    // Today when it falls inside the agreement, else its last allowed day.
    const pick = today >= agreement.date_from && today <= lastAllowed ? today : lastAllowed;
    setDate(pick < agreement.date_from ? agreement.date_from : pick);
    setReason(''); setError('');
  }, [agreement, today, lastAllowed]);
  const problem = !date ? 'Choose the last day the agreement covers.'
    : agreement && date < agreement.date_from ? `It can’t end before it starts (${fmtDate(agreement.date_from)}).`
      : agreement && date >= agreement.date_to ? `It already ends on ${fmtDate(agreement.date_to)}. To extend it, edit it instead.` : '';
  const go = async () => {
    if (problem) return;
    setBusy(true); setError('');
    try {
      const r = await api.post(`/sc/engagements/${coordinationId}/coordination-agreements/${agreement.id}/end`, { end_date: date, reason: reason.trim() });
      toast.success(`Agreement ends on ${fmtDate(date)}`);
      showAgreementWarnings(r.data && r.data.warnings);
      announce(coordinationId);
      onDone(r.data);
    } catch (e) { setError(errMsg(e, 'The agreement wasn’t ended. Try again.')); } finally { setBusy(false); }
  };
  return (
    <Modal open={!!agreement} onClose={onClose} title="End this agreement?" width="sm"
      sub={agreement ? `${fmtDate(agreement.date_from)} – ${fmtDate(agreement.date_to)} · ${perHour(agreement.hourly_rate)}` : undefined}
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={busy} disabled={!!problem} onClick={go}>End agreement</Button></>}>
      <div className="space-y-4">
        <p className="text-[13.5px] text-slate-600 leading-relaxed">The agreement stays on file and still gives the rate for every day up to its last day. Time after that waits until another agreement covers it.</p>
        <Field label="Last day it covers" required error={problem && date ? problem : ''}>
          <Input type="date" value={date} min={agreement ? agreement.date_from : undefined} max={lastAllowed || undefined} onChange={(e) => { setDate(e.target.value); setError(''); }} />
        </Field>
        <Field label="Reason" hint="Optional. Kept in the audit trail.">
          <Textarea rows={2} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        {error && <Banner tone="red">{error}</Banner>}
      </div>
    </Modal>
  );
}

// What needs doing when nothing covers today (the setup checklist says the same).
function coverageNotice(list) {
  const live = list.filter((a) => a.state !== 'cancelled');
  if (!live.length) return null;
  const current = live.find((a) => a.state === 'current' && Number(a.hourly_rate) > 0 && a.date_from && a.date_to);
  if (current) return null;
  const ended = live.filter((a) => a.state === 'ended').sort((a, b) => String(b.date_to || '').localeCompare(String(a.date_to || '')))[0];
  const future = live.filter((a) => a.state === 'future').sort((a, b) => String(a.date_from || '').localeCompare(String(b.date_from || '')))[0];
  const cur = live.find((a) => a.state === 'current');
  if (cur) return { title: 'The current agreement is missing its dates or rate', body: 'Edit it so claims can use it.', fix: cur };
  if (ended && future) return { title: 'No agreement covers today', body: `One ended on ${fmtDate(ended.date_to)} and the next starts on ${fmtDate(future.date_from)}. Change the dates so one covers today.`, fix: future };
  if (future) return { title: 'No agreement covers today', body: `The agreement starts on ${fmtDate(future.date_from)}. Change its start date or add one for now.`, fix: future };
  return { title: `The agreement ended on ${fmtDate(ended.date_to)}`, body: 'Extend it or add a new one. Claims are on hold until an agreement covers today.', fix: ended, extend: true };
}

export default function ServiceAgreementPanel({ coordinationId, perms = [], onChanged }) {
  const l = useLoad(coordinationId ? `/sc/engagements/${coordinationId}/coordination-agreements` : null);
  const [editor, setEditor] = useState(null);   // { agreement } | { add: true }
  const [ending, setEnding] = useState(null);
  const [flash, setFlash] = useState(false);
  const [sp] = useSearchParams();
  const box = useRef(null);
  const editable = canChange(perms);
  const list = useMemo(() => (l.data && l.data.agreements) || [], [l.data]);
  const priceLimits = l.data && l.data.price_limits;
  const notice = coverageNotice(list);

  // ?focus=agreement (or #service-agreement) brings the panel into view, e.g. from the Claims Fix list.
  const wantFocus = sp.get('focus') === 'agreement' || (typeof window !== 'undefined' && window.location.hash === '#service-agreement');
  const loaded = !!l.data;
  useEffect(() => {
    if (!wantFocus || !loaded || !box.current) return undefined;
    box.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    box.current.focus({ preventScroll: true });
    setFlash(true);
    const t = setTimeout(() => setFlash(false), 2400);
    return () => clearTimeout(t);
  }, [wantFocus, loaded]);

  const { reload } = l;
  useEffect(() => {
    const on = (e) => { if (!e.detail || e.detail.coordinationId === coordinationId) reload(); };
    window.addEventListener(SAVED_EVENT, on);
    return () => window.removeEventListener(SAVED_EVENT, on);
  }, [coordinationId, reload]);
  // The panel reloads through the saved event; the file reloads its checklist and forecast.
  const saved = () => { setEditor(null); setEnding(null); onChanged?.(); };
  // A new agreement starts the day after the latest one ends (no gap), at the same rate.
  const latest = list.filter((a) => a.state !== 'cancelled').sort((a, b) => String(b.date_to || '').localeCompare(String(a.date_to || '')))[0];
  const addDefaults = {
    date_from: latest && latest.date_to && latest.date_to < todayIso() ? addDays(latest.date_to, 1) : todayIso(),
    hourly_rate: latest ? latest.hourly_rate : '',
  };
  const addButton = editable && <LinkButton icon={Plus} onClick={() => setEditor({ add: true })}>Add agreement</LinkButton>;

  return (
    <div id="service-agreement" ref={box} tabIndex={-1} role="region" aria-label="Service agreement"
      className={clsx('scroll-mt-24 rounded-2xl outline-none transition-shadow duration-500', flash && 'ring-2 ring-amber-300 ring-offset-2')}>
      <Panel icon={FileSignature} title="Service agreement" sub="The dates and hourly rate this participant agreed to. Claims and invoices use them." actions={addButton}>
        {l.error ? <ErrorBlock error={l.error} onRetry={l.reload} /> : !l.data ? <LoadingBlock rows={1} /> : (
          <div className="space-y-3">
            {notice && (
              <Banner tone="amber" title={notice.title}
                action={editable && <Button size="sm" variant="secondary" onClick={() => setEditor({ agreement: notice.fix })}>{notice.extend ? 'Extend' : 'Edit'}</Button>}>
                {notice.body}
              </Banner>
            )}
            {!list.length ? (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-amber-50 px-4 py-3">
                <p className="text-[13px] text-amber-900">No service agreement yet. Claims wait until one with dates and an hourly rate covers today.</p>
                {editable && <Button size="sm" icon={Plus} onClick={() => setEditor({ add: true })}>Add agreement</Button>}
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {list.map((a) => {
                  const st = STATE[a.state] || STATE.current;
                  const inForce = a.state !== 'cancelled' && !(a.status && ['ended', 'cancelled'].includes(String(a.status).toLowerCase()));
                  return (
                    <li key={a.id} className="py-3 flex items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                          <Pill tone={st.tone} label={st.label} size="xs" />
                          <span className="text-[13.5px] font-semibold text-slate-900 whitespace-nowrap">{fmtDate(a.date_from)} – {fmtDate(a.date_to)}</span>
                          <span className="text-[13.5px] text-slate-700 tabular-nums whitespace-nowrap">{a.hourly_rate ? perHour(a.hourly_rate) : 'No rate'}</span>
                        </div>
                        {a.ended_at && (
                          <p className="text-[12.5px] text-slate-600 mt-1">Ended before its original end date{a.end_reason ? `: ${a.end_reason}` : '.'}</p>
                        )}
                        {a.notes && <p className="text-[12.5px] text-slate-500 mt-1 whitespace-pre-wrap break-words">{a.notes}</p>}
                      </div>
                      {editable && inForce && (
                        <RowMenu label={`Actions for the agreement ${fmtDate(a.date_from)} to ${fmtDate(a.date_to)}`} items={[
                          { label: a.state === 'ended' ? 'Edit or extend' : 'Edit', icon: Pencil, onClick: () => setEditor({ agreement: a }) },
                          a.date_to && a.date_from && a.date_to > a.date_from ? { label: 'End on a date', icon: CalendarX2, onClick: () => setEnding(a) } : null,
                        ]} />
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            {!editable && <p className="text-[12.5px] text-slate-500">Someone with the Manage referrals or Admin permission can change the agreement.</p>}
          </div>
        )}
      </Panel>

      <Sheet open={!!editor} onClose={() => setEditor(null)} icon={FileSignature}
        title={editor && editor.agreement ? 'Edit service agreement' : 'Add service agreement'}
        sub={editor && editor.agreement ? `${fmtDate(editor.agreement.date_from)} – ${fmtDate(editor.agreement.date_to)}` : 'The dates and hourly rate the participant agreed to.'}>
        {editor && (
          <AgreementForm key={editor.agreement ? editor.agreement.id : 'new'} coordinationId={coordinationId} agreement={editor.agreement || null}
            defaults={addDefaults} priceLimits={priceLimits} onSaved={saved} onCancel={() => setEditor(null)} />
        )}
      </Sheet>
      <EndModal coordinationId={coordinationId} agreement={ending} onClose={() => setEnding(null)} onDone={saved} />
    </div>
  );
}
